import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { securityKeys } from "../../enums/SecurityKeys.ENUM";
import { hasPermission } from "../../utils/authentication/authorizeNavigation";
import { buildPageContext } from "./copilotUtils";
import { useCopilotThreads } from "./useCopilotThreads";
import { useCopilotContextDisplay } from "./useCopilotContextDisplay";

const CopilotShellContext = createContext(null);

const isAdminDebugWorkspace = (location) => {
  const path = location?.pathname || "";
  if (!path.includes("recruitment-ai-assistant")) return false;
  const params = new URLSearchParams(location?.search || "");
  return params.get("debug") === "1";
};

export const CopilotShellProvider = ({ children }) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const location = useLocation();

  const rawContext = useMemo(
    () => buildPageContext(location),
    [location.pathname, location.search]
  );
  const pageContext = useCopilotContextDisplay(rawContext);

  const threadApi = useCopilotThreads(pageContext);
  const canUseDebug = hasPermission(securityKeys.AI_CONTROL_CENTER);
  const debugMode = canUseDebug && isAdminDebugWorkspace(location);

  const openDrawer = useCallback(() => setDrawerVisible(true), []);
  const closeDrawer = useCallback(() => setDrawerVisible(false), []);
  const toggleDrawer = useCallback(() => setDrawerVisible((v) => !v), []);

  useEffect(() => {
    if (!threadApi.activeThreadId && threadApi.threads.length === 0) {
      threadApi.createThread();
    }
  }, [threadApi.activeThreadId, threadApi.threads.length, threadApi.createThread]);

  const value = useMemo(
    () => ({
      drawerVisible,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      pageContext,
      threads: threadApi,
      canUseDebug,
      debugMode,
    }),
    [
      drawerVisible,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      pageContext,
      threadApi,
      canUseDebug,
      debugMode,
    ]
  );

  return (
    <CopilotShellContext.Provider value={value}>
      {children}
    </CopilotShellContext.Provider>
  );
};

export const useCopilotShell = () => {
  const ctx = useContext(CopilotShellContext);
  if (!ctx) {
    throw new Error("useCopilotShell must be used within CopilotShellProvider");
  }
  return ctx;
};

export default CopilotShellContext;
